import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Camera,
  Upload,
  Copy,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Loader2,
  StopCircle,
  QrCode,
  Link as LinkIcon,
  Mail,
  Phone,
  Wifi,
  FileText,
  ShieldAlert,
  ExternalLink,
  ScanBarcode,
} from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { TableScroll } from "@/components/layout/ContentBlock";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FaqSection } from "@/components/seo/FaqSection";
import { toast } from "sonner";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, softwareAppJsonLd, breadcrumbJsonLd, ldScript } from "@/lib/seo";
import {
  decodeFromFile,
  formatLabel,
  startCameraScan,
  type CameraController,
  type DecodeResult,
} from "@/lib/zxing";

const PATH = "/qr-code-reader";
const TITLE = "QR Code Reader — Scan & Decode QR Codes Online";
const DESC =
  "Free online QR code reader. Scan with your camera or upload an image to decode URLs, Wi-Fi, email, phone and plain text — safe in your browser.";

const FAQ = [
  {
    q: "Does the QR reader send my data anywhere?",
    a: "No. The camera stream and any uploaded image are decoded locally in your browser using the ZXing JavaScript library. We never upload or store what you scan.",
  },
  {
    q: "Will scanning a QR code open the link automatically?",
    a: "Never. ReadBarcode always shows the decoded URL first so you can inspect the host and path before deciding to open it. This protects you from QR phishing (quishing).",
  },
  {
    q: "What Kinds of Content Can QR Codes Contain?",
    a: "QR codes hold any short text. Common categories are URLs, Wi-Fi credentials (WIFI:...), email (mailto:), phone (tel:), SMS, contact cards (vCard / MECARD) and plain text.",
  },
  {
    q: "Can I read QR codes from a screenshot?",
    a: "Yes. Switch to the Image tab and drop in any PNG, JPG or WEBP screenshot. The decoder runs in your browser.",
  },
  {
    q: "What if I need to read a 1D barcode or a Data Matrix?",
    a: "Use the Online Barcode Reader for UPC/EAN and other 1D barcodes, or the Data Matrix Reader for 2D Data Matrix labels. Links are below.",
  },
  {
    q: "Is a QR Code Reader Free?",
    a: "This one is: there is no account, no payment and no limit on how many codes you scan. Decoding runs in your browser, so nothing you scan is sent to a server. Most phone camera apps also read QR codes at no cost.",
  },
  {
    q: "Can I Use a QR Code Reader on My Computer?",
    a: "Yes. On a laptop or desktop, use the Camera tab with a webcam, or the Image tab with a screenshot or a saved file. The Image tab is often easier, because you do not have to hold the code steady in front of the webcam. Camera access needs a one-time browser permission.",
  },
  {
    q: "How Do I Read a QR Code Online?",
    a: "Open this page, choose Camera or Image, then point the camera at the code or drop in a picture of it. The decoded text appears in the result panel with its type, such as URL or Wi-Fi. Links are never opened for you, so check the host before you use the Open in new tab button.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Readers", path: "/readers" },
  { name: "QR Code Reader", path: PATH },
];

export const Route = createFileRoute("/qr-code-reader")({
  component: QrReaderPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode QR Code Reader",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

type Status =
  | { kind: "idle" }
  | { kind: "loading"; label: string }
  | { kind: "scanning" }
  | { kind: "success"; result: DecodeResult }
  | { kind: "error"; message: string };

function QrReaderPage() {
  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="QR Code Reader"
      description="Scan a QR code with your camera or upload an image. URLs are shown safely — we never redirect automatically. Every result shows the raw decoded text with a copy button, and Wi-Fi, email, phone and SMS payloads are split into labeled fields."
      crumbs={CRUMBS}
      content={
        <>
          <SafetyNote />
          <ErrorCorrectionGuide />
          <ToolSeoSections
            aboutHeading="About the QR code reader"
            about={
              <>
                <p>
                  This QR code reader decodes QR codes from your camera or an uploaded image, all inside
                  your browser. It recognises common QR payload types — URLs, plain text, Wi‑Fi
                  credentials, email, phone and SMS — and shows them in a safe, readable format. We
                  never redirect automatically; you choose whether to open a scanned link.
                </p>
                <p>
                  It's a deliberately small, focused QR scanner online: no signup, no tracking, no app
                  store install required.
                </p>
              </>
            }
            whenToUse={[
              "Reading a QR code on a poster, menu or product without opening your phone's app.",
              "Scanning a Wi‑Fi QR code at a café and copying the password manually.",
              "Checking a suspicious QR code (quishing) before opening the URL.",
              "Decoding a contact QR or SMS QR for review before acting on it.",
              "Testing QR codes you generated to make sure they encode the right payload.",
            ]}
            howItWorks={[
              {
                title: "Pick camera or upload",
                desc: "Switch tabs depending on whether you have the code in front of you or as a file.",
              },
              {
                title: "Scan or drop",
                desc: "Point the camera at the QR or drop the image into the upload area.",
              },
              {
                title: "We categorize the payload",
                desc: "URL, text, Wi‑Fi, email, phone or SMS — each gets its own safe view.",
              },
              {
                title: "Inspect before acting",
                desc: "Verify the host, the SSID or the phone number before opening anything.",
              },
              {
                title: "Copy what you need",
                desc: "Copy the value or, for URLs, click through manually.",
              },
            ]}
            whatToAvoid={[
              "Opening QR links blindly — quishing attacks rely on auto-redirect scanners.",
              "Trusting a QR code stuck over an existing one in public (common parking-meter scam).",
              "Assuming a Wi‑Fi QR is safe just because it scans — verify the SSID.",
              "Photographing QR codes at sharp angles or under heavy glare.",
              "Confusing a QR with a 1D UPC/EAN — use the barcode reader for product codes.",
            ]}
            tips={[
              "Always check the URL host before you click through.",
              "On phones, hold steady about 10–15 cm from the code for a quick decode.",
              "For QR codes on screens, lower brightness slightly to remove reflections.",
              "If a code keeps failing, take a photo and use the upload tab instead.",
              "Use the copy button to paste a Wi‑Fi password straight into the settings app.",
            ]}
          />
          <h2>QR Code Decoder: Turn a QR Code Into Text</h2>
          <p>This QR code decoder shows the exact text a QR code stores in the Raw value box, next to its own reading of the payload type. Use it as a free QR code online decoder: decode QR code images from the camera or a file, and analyze QR code contents such as Wi-Fi, email and phone fields before you act on them.</p>
          <h3>Read a QR Code From an Image</h3>
          <p>To decode a QR code from a file, open the Image tab and drop in a photo or screenshot; it is decoded in your browser and never uploaded. If the first pass fails, the reader retries with a white border added, which helps tightly cropped codes that lost their quiet zone.</p>
          <h3>QR to Text Without Opening the Link</h3>
          <p>Links stop at the text: you see the full URL and its host, with a warning when it is not HTTPS. A GS1 Digital Link code decodes to a web address with the product's GTIN after /01/, as our <Link to="/barcode-guides/gs1-digital-link">GS1 Digital Link guide</Link> explains.</p>
          <h2>How a QR Code Reader Works</h2>
          <p>A QR code reader finds the three finder patterns, samples the grid of modules, repairs errors and converts the data back into text, as set out in ISO/IEC 18004. This online QR code reader does so on each camera frame.</p>
          <ol>
            <li>Locate the finder patterns to get position, size and rotation.</li>
            <li>Read the format information: error correction level and mask pattern.</li>
            <li>Sample the modules, remove the mask and apply Reed–Solomon correction.</li>
            <li>Decode the data mode (numeric, alphanumeric, byte or kanji) into text.</li>
          </ol>
          <p>Test the other direction with the <Link to="/qr-code-generator">QR code generator</Link>: make a code, then scan it back here to check the payload.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <QrReaderApp />
      <CategoryGuide />
    </ToolLayout>
  );
}

function QrReaderApp() {
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [tab, setTab] = useState<"camera" | "upload">("camera");

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controllerRef = useRef<CameraController | null>(null);
  // Monotonic token bumped on every stop. `startCameraScan` is async
  // (getUserMedia); if a stop/unmount fires while the stream is still
  // negotiating, the captured token goes stale and the resolved controller is
  // released immediately instead of leaking a live camera (hardware light
  // stuck on).
  const startIdRef = useRef(0);

  const stopCamera = useCallback(() => {
    startIdRef.current += 1;
    controllerRef.current?.stop();
    controllerRef.current = null;
  }, []);

  useEffect(() => {
    if (tab !== "camera") stopCamera();
    return () => stopCamera();
  }, [tab, stopCamera]);

  const handleStartCamera = async () => {
    if (!videoRef.current) return;
    stopCamera();
    const startId = startIdRef.current;
    setStatus({ kind: "loading", label: "Requesting camera…" });
    try {
      const ctrl = await startCameraScan(
        videoRef.current,
        (r) => {
          stopCamera();
          setStatus({ kind: "success", result: r });
        },
        (err) => {
          stopCamera();
          setStatus({ kind: "error", message: err.message });
        },
      );
      if (startId !== startIdRef.current) {
        // Stopped/unmounted while starting — release the stream, don't store it.
        ctrl.stop();
        return;
      }
      controllerRef.current = ctrl;
      setStatus({ kind: "scanning" });
    } catch (err) {
      if (startId !== startIdRef.current) return; // stale failure — ignore
      const msg =
        err instanceof Error
          ? err.name === "NotAllowedError"
            ? "Camera permission denied. Allow camera access and try again."
            : err.message
          : "Could not start the camera.";
      setStatus({ kind: "error", message: msg });
    }
  };

  const handleFile = async (file: File) => {
    setStatus({ kind: "loading", label: "Decoding image…" });
    try {
      const result = await decodeFromFile(file);
      setStatus({ kind: "success", result });
    } catch {
      setStatus({
        kind: "error",
        message:
          "No QR code found in that image. Try a clearer screenshot or photo where the QR fills most of the frame.",
      });
    }
  };

  const reset = () => {
    stopCamera();
    setStatus({ kind: "idle" });
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
      <Card>
        <CardContent className="p-5 md:p-6">
          <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="camera">
                <Camera className="mr-2 h-4 w-4" />
                Camera
              </TabsTrigger>
              <TabsTrigger value="upload">
                <Upload className="mr-2 h-4 w-4" />
                Image
              </TabsTrigger>
            </TabsList>

            <TabsContent value="camera" className="mt-5">
              <CameraPanel
                videoRef={videoRef}
                status={status}
                onStart={handleStartCamera}
                onStop={() => {
                  stopCamera();
                  setStatus({ kind: "idle" });
                }}
              />
            </TabsContent>

            <TabsContent value="upload" className="mt-5">
              <UploadPanel onFile={handleFile} status={status} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <ResultPanel status={status} onReset={reset} />
    </div>
  );
}

function CameraPanel({
  videoRef,
  status,
  onStart,
  onStop,
}: {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  status: Status;
  onStart: () => void;
  onStop: () => void;
}) {
  const scanning = status.kind === "scanning";
  const loading = status.kind === "loading";
  return (
    <div>
      <div className="relative overflow-hidden rounded-lg border border-border bg-muted">
        <video
          ref={videoRef}
          className="aspect-video w-full bg-black object-cover"
          playsInline
          muted
        />
        {!scanning && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/80 p-6 text-center">
            <QrCode className="h-8 w-8 text-muted-foreground" aria-hidden />
            <div className="text-sm text-muted-foreground">
              Point your camera at a QR code. We'll decode it automatically.
            </div>
          </div>
        )}
        {scanning && (
          <div className="pointer-events-none absolute inset-x-10 inset-y-6 rounded-md border-2 border-primary/70 shadow-[0_0_24px_var(--color-primary)]" />
        )}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {!scanning ? (
          <Button onClick={onStart} disabled={loading}>
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Camera className="mr-2 h-4 w-4" />
            )}
            Start camera
          </Button>
        ) : (
          <Button onClick={onStop} variant="outline">
            <StopCircle className="mr-2 h-4 w-4" /> Stop camera
          </Button>
        )}
        <p className="self-center text-xs text-muted-foreground">
          Camera access requires HTTPS and a one-time permission.
        </p>
      </div>
    </div>
  );
}

function UploadPanel({ onFile, status }: { onFile: (file: File) => void; status: Status }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const loading = status.kind === "loading";

  return (
    <div>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) onFile(file);
        }}
        className={`flex aspect-video w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
          dragOver ? "border-primary bg-primary/5" : "border-border bg-muted/40 hover:bg-muted"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
            e.target.value = "";
          }}
        />
        <Upload className="h-8 w-8 text-muted-foreground" aria-hidden />
        <div className="text-sm">
          <span className="font-medium">Drop a QR image</span>
          <span className="text-muted-foreground"> or tap to choose</span>
        </div>
        <div className="text-xs text-muted-foreground">PNG · JPG · WEBP · BMP</div>
      </label>
      <div className="mt-4 flex items-center gap-2">
        <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={loading}>
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Upload className="mr-2 h-4 w-4" />
          )}
          Choose image
        </Button>
        <p className="text-xs text-muted-foreground">Decoded locally — never uploaded.</p>
      </div>
    </div>
  );
}

type Category =
  | { kind: "url"; url: string; host: string; safe: boolean }
  | { kind: "email"; to: string; subject?: string; body?: string }
  | { kind: "phone"; number: string }
  | { kind: "sms"; number: string; body?: string }
  | { kind: "wifi"; ssid: string; auth: string; password?: string; hidden: boolean; raw: string }
  | { kind: "text"; text: string }
  | { kind: "unknown"; text: string };

function categorize(text: string): Category {
  const t = text.trim();
  // URL
  if (/^https?:\/\//i.test(t)) {
    try {
      const u = new URL(t);
      const safe = u.protocol === "https:";
      return { kind: "url", url: u.toString(), host: u.host, safe };
    } catch {
      // fall through
    }
  }
  // mailto
  if (/^mailto:/i.test(t)) {
    const rest = t.slice(7);
    const [to, query = ""] = rest.split("?");
    const params = new URLSearchParams(query);
    return {
      kind: "email",
      to,
      subject: params.get("subject") ?? undefined,
      body: params.get("body") ?? undefined,
    };
  }
  // tel
  if (/^tel:/i.test(t)) return { kind: "phone", number: t.slice(4) };
  // sms
  if (/^smsto?:/i.test(t)) {
    const rest = t.replace(/^smsto?:/i, "");
    const [num, body] = rest.split(/[:?]/, 2);
    return { kind: "sms", number: num, body };
  }
  // Wi-Fi: WIFI:T:WPA;S:mynet;P:mypass;H:false;;
  if (/^WIFI:/i.test(t)) {
    const body = t.slice(5).replace(/;;$/, "");
    const parts = body.split(";").filter(Boolean);
    const map: Record<string, string> = {};
    for (const p of parts) {
      const idx = p.indexOf(":");
      if (idx > 0) map[p.slice(0, idx).toUpperCase()] = p.slice(idx + 1);
    }
    return {
      kind: "wifi",
      ssid: map.S ?? "",
      auth: (map.T ?? "nopass").toUpperCase(),
      password: map.P,
      hidden: /^true$/i.test(map.H ?? "false"),
      raw: t,
    };
  }
  // Plain printable text
  if (/^[\x20-\x7E\s]+$/.test(t)) return { kind: "text", text: t };
  return { kind: "unknown", text: t };
}

function ResultPanel({ status, onReset }: { status: Status; onReset: () => void }) {
  const category = useMemo(
    () => (status.kind === "success" ? categorize(status.result.text) : null),
    [status],
  );

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  return (
    <Card>
      <CardContent className="flex h-full flex-col p-5 md:p-6">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Result
          </p>
          <Button variant="ghost" size="sm" onClick={onReset}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" /> Clear
          </Button>
        </div>

        <div className="mt-4 flex-1">
          {status.kind === "idle" && (
            <EmptyState>
              Start the camera or drop a QR image. The decoded value will appear here — we never
              auto-open links.
            </EmptyState>
          )}

          {(status.kind === "loading" || status.kind === "scanning") && (
            <CenterState>
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <div className="text-sm text-muted-foreground">
                {status.kind === "loading" ? status.label : "Scanning for a QR code…"}
              </div>
            </CenterState>
          )}

          {status.kind === "error" && (
            <div className="flex flex-col items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Couldn't decode</span>
              </div>
              <p className="text-sm text-muted-foreground">{status.message}</p>
            </div>
          )}

          {status.kind === "success" && category && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-sm font-medium">Decoded</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{formatLabel(status.result.format)}</Badge>
                <CategoryBadge category={category} />
              </div>
              <CategoryView category={category} onCopy={copy} />
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Raw value
                </div>
                <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-md border border-border bg-muted/40 p-3 font-mono text-xs">
                  {status.result.text}
                </pre>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => copy(status.result.text)}>
                  <Copy className="mr-2 h-3.5 w-3.5" /> Copy raw value
                </Button>
                <Button size="sm" variant="outline" onClick={onReset}>
                  Scan another
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function CategoryBadge({ category }: { category: Category }) {
  const map: Record<
    Category["kind"],
    { label: string; icon: React.ComponentType<{ className?: string }> }
  > = {
    url: { label: "URL", icon: LinkIcon },
    email: { label: "Email", icon: Mail },
    phone: { label: "Phone", icon: Phone },
    sms: { label: "SMS", icon: Phone },
    wifi: { label: "Wi-Fi", icon: Wifi },
    text: { label: "Text", icon: FileText },
    unknown: { label: "Unknown", icon: ShieldAlert },
  };
  const { label, icon: Icon } = map[category.kind];
  return (
    <Badge variant="outline" className="gap-1">
      <Icon className="h-3.5 w-3.5" />
      {label}
    </Badge>
  );
}

function CategoryView({ category, onCopy }: { category: Category; onCopy: (s: string) => void }) {
  switch (category.kind) {
    case "url":
      return (
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Link</div>
          <div className="mt-1 break-all font-mono text-sm">{category.url}</div>
          <div className="mt-2 text-xs text-muted-foreground">
            Host: <span className="font-medium text-foreground">{category.host}</span>
            {!category.safe && (
              <span className="ml-2 inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                <ShieldAlert className="h-3.5 w-3.5" /> Not HTTPS — verify before opening.
              </span>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => onCopy(category.url)}>
              <Copy className="mr-2 h-3.5 w-3.5" /> Copy link
            </Button>
            <Button asChild size="sm" variant="secondary">
              <a href={category.url} target="_blank" rel="noopener noreferrer nofollow ugc">
                <ExternalLink className="mr-2 h-3.5 w-3.5" /> Open in new tab
              </a>
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            ReadBarcode never redirects you automatically. Check the host above before clicking.
          </p>
        </div>
      );
    case "email":
      return (
        <Field label="Email">
          <Row label="To" value={category.to} onCopy={onCopy} />
          {category.subject && <Row label="Subject" value={category.subject} onCopy={onCopy} />}
          {category.body && <Row label="Body" value={category.body} onCopy={onCopy} />}
        </Field>
      );
    case "phone":
      return (
        <Field label="Phone number">
          <Row label="Number" value={category.number} onCopy={onCopy} />
        </Field>
      );
    case "sms":
      return (
        <Field label="SMS">
          <Row label="Number" value={category.number} onCopy={onCopy} />
          {category.body && <Row label="Message" value={category.body} onCopy={onCopy} />}
        </Field>
      );
    case "wifi":
      return (
        <Field label="Wi-Fi network">
          <Row label="SSID" value={category.ssid || "(empty)"} onCopy={onCopy} />
          <Row label="Security" value={category.auth} onCopy={onCopy} />
          {category.password && <Row label="Password" value={category.password} onCopy={onCopy} />}
          <Row label="Hidden" value={category.hidden ? "Yes" : "No"} onCopy={onCopy} />
          <p className="mt-2 text-xs text-muted-foreground">
            Browsers can't connect to Wi-Fi automatically — copy the password into your device
            settings.
          </p>
        </Field>
      );
    case "text":
      return (
        <Field label="Text">
          <pre className="whitespace-pre-wrap break-words font-mono text-sm">{category.text}</pre>
        </Field>
      );
    case "unknown":
      return (
        <Field label="Unknown content">
          <p className="text-sm text-muted-foreground">
            This QR code doesn't match a recognized category. The raw value is below.
          </p>
        </Field>
      );
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-2 space-y-2">{children}</div>
    </div>
  );
}

function Row({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (s: string) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="break-all font-mono text-sm">{value}</div>
      </div>
      <Button size="sm" variant="ghost" onClick={() => onCopy(value)} aria-label={`Copy ${label}`}>
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-32 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
      <QrCode className="h-7 w-7 text-muted-foreground" aria-hidden />
      <p className="max-w-sm text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function CenterState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-32 flex-col items-center justify-center gap-2 rounded-lg border border-border bg-muted/30 p-6 text-center">
      {children}
    </div>
  );
}

function CategoryGuide() {
  const cats = [
    { icon: LinkIcon, label: "URL", desc: "Web links — shown for review, never auto-opened." },
    { icon: FileText, label: "Plain Text", desc: "Any short text payload, copied with one tap." },
    { icon: Mail, label: "Email", desc: "mailto: with optional subject and body." },
    { icon: Phone, label: "Phone", desc: "tel: numbers ready to dial on mobile." },
    { icon: Wifi, label: "Wi-Fi", desc: "WIFI: payloads parsed into SSID, security and password." },
    {
      icon: ShieldAlert,
      label: "Unknown",
      desc: "Anything else is shown as the raw decoded value.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-2xl font-semibold tracking-tight">What QR Codes Can Contain</h2>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        QR codes are just short text. ReadBarcode detects the most common categories and shows them
        in a structured view.
      </p>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cats.map((c) => (
          <div key={c.label} className="rounded-lg border border-border bg-background p-4">
            <div className="flex items-center gap-2">
              <c.icon className="h-4 w-4 text-primary" aria-hidden />
              <h3 className="text-sm font-semibold">{c.label}</h3>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{c.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ErrorCorrectionGuide() {
  const levels = [
    { level: "L (Low)", recovers: "~7%", use: "Clean digital screens and pristine prints." },
    { level: "M (Medium)", recovers: "~15%", use: "The common default for posters and packaging." },
    {
      level: "Q (Quartile)",
      recovers: "~25%",
      use: "Codes that may pick up scuffs or a small logo overlay.",
    },
    {
      level: "H (High)",
      recovers: "~30%",
      use: "Harsh environments, stickers, or logo-in-the-middle designs.",
    },
  ];
  return (
    <>
      <h2>Why a Scratched QR Code Still Scans</h2>
      <p>
        QR codes use Reed–Solomon error correction, so part of the code can be dirty, torn or
        covered by a logo and still decode. The chosen correction level sets how much damage is
        recoverable. If a code won't read, it's usually framing or focus — not missing data — so
        steady the camera and fill the frame before assuming the code is broken.
      </p>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Level</th>
              <th scope="col">Recoverable</th>
              <th scope="col">Best for</th>
            </tr>
          </thead>
          <tbody>
            {levels.map((l) => (
              <tr key={l.level}>
                <td>{l.level}</td>
                <td>{l.recovers}</td>
                <td>{l.use}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </>
  );
}

function SafetyNote() {
  return (
    <>
      <h2>Safe Link Handling</h2>
      <p>
        Malicious QR codes (sometimes called "quishing") can hide phishing links behind a
        friendly sticker. ReadBarcode never opens scanned URLs automatically. We show the full
        link and the host so you can verify the destination before clicking. External links open
        in a new tab with <code>rel="noopener noreferrer nofollow"</code>.
      </p>
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/barcode-reader",
      label: "Online Barcode Reader",
      desc: "Scan UPC, EAN and 1D barcodes with your camera.",
      icon: Camera,
    },
    {
      path: "/read-barcode-from-image",
      label: "Read Barcode From Image",
      desc: "Upload a photo to decode any barcode.",
      icon: Upload,
    },
    {
      path: "/data-matrix-reader",
      label: "Data Matrix Reader",
      desc: "Decode 2D Data Matrix labels used in healthcare and logistics.",
      icon: ScanBarcode,
    },
    {
      path: "/gtin-validator",
      label: "GTIN Validator",
      desc: "Verify GTIN-8/12/13/14 check digits.",
      icon: ScanBarcode,
    },
    {
      path: "/qr-code-generator",
      label: "QR Code Generator",
      desc: "Create a QR code for a link, text or Wi-Fi network.",
      icon: QrCode,
    },
    {
      path: "/barcode-guides/gs1-digital-link",
      label: "GS1 Digital Link Guide",
      desc: "How product QR codes carry a GTIN inside a web address.",
      icon: LinkIcon,
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Need a Different Tool?</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group flex items-start gap-3 rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <r.icon className="mt-0.5 h-4 w-4 text-primary" />
            <div>
              <div className="text-sm font-medium group-hover:text-primary">{r.label}</div>
              <div className="text-xs text-muted-foreground">{r.desc}</div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
